/**
 * WorkflowExecutionsPage — audit log of workflow rule executions.
 */

import { useEffect, useState } from "react";
import type { ExecutionStatus, WorkflowExecution } from "../../types/workflows";
import { apiFetch } from "../../lib/config";

const STATUS_BADGE: Record<ExecutionStatus, string> = {
  success: "ac-badge--operational",
  failed: "ac-badge--outage",
  skipped: "ac-badge--neutral",
  pending: "ac-badge--degraded",
  running: "ac-badge--info",
};

function durationLabel(started: string, completed: string | null): string {
  if (!completed) return "--";
  const ms = new Date(completed).getTime() - new Date(started).getTime();
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function WorkflowExecutionsPage() {
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStatus) params.set("status", filterStatus);
    if (filterFrom) params.set("from", filterFrom);
    if (filterTo) params.set("to", filterTo);
    try {
      const res = await apiFetch(`/api/v1/workflows/executions/?${params}`);
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setExecutions(Array.isArray(data) ? data : data.results ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterStatus, filterFrom, filterTo]);

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Workflow Executions</h1>
          <p className="ac-page-subtitle">Audit log of all workflow rule runs</p>
        </div>
        <div className="ac-page-header__actions">
          <button type="button" className="aptean-btn aptean-btn--secondary" onClick={load} disabled={loading}>Refresh</button>
        </div>
      </div>

      <div className="ac-filter-bar">
        <select
          className="aptean-input"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {(["pending", "running", "success", "failed", "skipped"] as ExecutionStatus[]).map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <input type="date" className="aptean-input" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} aria-label="From date" />
        <input type="date" className="aptean-input" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} aria-label="To date" />
        <span className="ac-filter-bar__sep" />
        {!loading && <span style={{ fontSize: "0.8125rem", color: "var(--aptean-text-secondary)" }}>{executions.length} execution{executions.length !== 1 ? "s" : ""}</span>}
      </div>

      {error && <div className="ac-alert ac-alert--error" style={{ marginBottom: "1rem" }}>{error}</div>}

      <div className="ac-table-wrap">
        <table className="ac-table">
          <thead>
            <tr>
              <th style={{ width: "30%" }}>Rule</th>
              <th>Status</th>
              <th>Triggered by</th>
              <th>Started</th>
              <th>Duration</th>
              <th className="ac-th--right">Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6}><div className="ac-empty"><div className="ac-empty__body">Loading executions...</div></div></td></tr>
            ) : executions.length === 0 ? (
              <tr><td colSpan={6}><div className="ac-empty"><div className="ac-empty__title">No executions found</div><div className="ac-empty__body">Adjust filters or trigger a workflow rule.</div></div></td></tr>
            ) : executions.map((exec) => (
              <>
                <tr key={exec.id}>
                  <td className="ac-row-title">{exec.rule.name}</td>
                  <td><span className={`ac-badge ${STATUS_BADGE[exec.status] ?? "ac-badge--neutral"}`}>{exec.status}</span></td>
                  <td className="ac-td--muted">{exec.triggered_by}</td>
                  <td className="ac-td--muted">{new Date(exec.started_at).toLocaleString()}</td>
                  <td className="ac-td--muted">{durationLabel(exec.started_at, exec.completed_at)}</td>
                  <td className="ac-td--right">
                    <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                      onClick={() => setExpanded(expanded === exec.id ? null : exec.id)}>
                      {expanded === exec.id ? "Hide" : "Expand"}
                    </button>
                  </td>
                </tr>
                {expanded === exec.id && (
                  <tr key={`${exec.id}-detail`} className="ac-detail-row">
                    <td colSpan={6}>
                      <div className="ac-detail-panel">
                        <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--aptean-text-secondary)" }}>Trigger data</div>
                        <pre style={{ margin: 0, fontSize: "0.75rem", color: "var(--aptean-text-primary)", fontFamily: "var(--aptean-font-mono)", overflow: "auto", background: "var(--aptean-bg-base)", padding: "0.625rem", borderRadius: "var(--aptean-radius-md)" }}>
                          {JSON.stringify(exec.trigger_data, null, 2)}
                        </pre>
                        {Object.keys(exec.result).length > 0 && (
                          <>
                            <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--aptean-text-secondary)" }}>Result</div>
                            <pre style={{ margin: 0, fontSize: "0.75rem", color: "var(--aptean-text-primary)", fontFamily: "var(--aptean-font-mono)", overflow: "auto", background: "var(--aptean-bg-base)", padding: "0.625rem", borderRadius: "var(--aptean-radius-md)" }}>
                              {JSON.stringify(exec.result, null, 2)}
                            </pre>
                          </>
                        )}
                        {exec.error && (
                          <div className="ac-alert ac-alert--error">{exec.error}</div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
